import React, { useState, useEffect, useMemo } from 'react';
import { Check, X, Clock, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { generateId, formatDate } from '../../utils/helpers';
import { api } from '../../utils/api';
import DateInput from '../../components/common/DateInput';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_LETTERS = ['S','M','T','W','T','F','S'];

function getMonthRange(year, month) {
  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const last = new Date(year, month, 0).getDate();
  const end = `${year}-${String(month).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { start, end };
}

function daysInMonthArr(year, month) {
  const n = new Date(year, month, 0).getDate();
  return Array.from({ length: n }, (_, i) => {
    const day = i + 1;
    return {
      day,
      dateStr: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      dow: new Date(year, month - 1, day).getDay(),
    };
  });
}

// Approved leave / excused don't count against the % — same convention as
// Attendance Reports and Results.
const GRID_STATUS_CYCLE = [null, 'present', 'absent', 'late', 'leave', 'excused'];
const GRID_SHORT = { present: 'P', absent: 'A', late: 'L', leave: 'Lv', excused: 'Ex' };
const GRID_COLOR = { present: '#16a34a', absent: '#dc2626', late: '#f59e0b', leave: '#6366f1', excused: '#06b6d4' };

function StudentAttendance() {
  const { attendance, setAttendance, students, classes, academicYear } = useApp();
  const { currentUser } = useAuth();
  const toast = useToast();
  const isTeacher = currentUser?.role === 'teacher';
  const availableClasses = isTeacher
    ? classes.filter(c => c.classTeacherId === currentUser?.profileId)
    : classes;
  // If this teacher matches more than one class (e.g. a stale prior-year
  // assignment never cleared), default to the current academic year's one
  // rather than whichever happens to come first.
  const defaultClass = availableClasses.find(c => c.academicYear === academicYear) || availableClasses[0];
  const today = new Date().toISOString().split('T')[0];
  const [selectedClass, setSelectedClass] = useState(defaultClass?.id || '');

  // 'daily' = the original one-date-at-a-time marking screen.
  // 'monthly' = a full month × every student grid, all in one save.
  const [viewMode, setViewMode] = useState('daily');

  const [date, setDate] = useState(today);
  const [localAttendance, setLocalAttendance] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const classStudents = useMemo(() =>
    students.filter(s => s.classId === selectedClass && s.status === 'active')
      .sort((a, b) => Number(a.rollNumber) - Number(b.rollNumber)),
    [students, selectedClass]);

  React.useEffect(() => {
    const existing = attendance.filter(a => a.date === date && a.classId === selectedClass && a.entityType === 'student');
    const map = {};
    existing.forEach(a => { map[a.entityId] = a.status; });
    classStudents.forEach(s => { if (!map[s.id]) map[s.id] = 'present'; });
    setLocalAttendance(map);
    setSaved(false);
  }, [selectedClass, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = (studentId, status) => setLocalAttendance(prev => ({ ...prev, [studentId]: status }));

  const saveAttendance = async () => {
    const filtered = attendance.filter(a => !(a.date === date && a.classId === selectedClass && a.entityType === 'student'));
    const newRecords = classStudents.map(s => ({
      id: generateId('ATT'), date, entityType: 'student', entityId: s.id,
      classId: selectedClass, status: localAttendance[s.id] || 'present', markedBy: currentUser?.profileId || 'STF-001',
      academicYear: academicYear || '',
    }));
    setSaving(true);
    try {
      await api.post('/attendance/bulk', { records: newRecords });
      setAttendance([...filtered, ...newRecords]);
      toast?.success('Attendance saved');
    } catch (e) {
      toast?.error(`Save failed: ${e.message}`);
      setSaving(false);
      return;
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // ── Monthly Grid ──────────────────────────────────────────────
  const now = new Date();
  const [gridMonthYear, setGridMonthYear] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const prevGridMonth = () => setGridMonthYear(p => p.m === 1 ? { y: p.y - 1, m: 12 } : { y: p.y, m: p.m - 1 });
  const nextGridMonth = () => setGridMonthYear(p => p.m === 12 ? { y: p.y + 1, m: 1 } : { y: p.y, m: p.m + 1 });
  const gridDays = useMemo(() => daysInMonthArr(gridMonthYear.y, gridMonthYear.m), [gridMonthYear]);
  const { start: gridStart, end: gridEnd } = useMemo(() => getMonthRange(gridMonthYear.y, gridMonthYear.m), [gridMonthYear]);

  // { studentId: { 'YYYY-MM-DD': status } } — rebuilt from already-loaded
  // attendance whenever the class or month changes, not on every attendance
  // change, so an in-progress edit isn't clobbered by an unrelated refresh.
  const [gridData, setGridData] = useState({});
  const [gridSaving, setGridSaving] = useState(false);
  const [gridSaved, setGridSaved] = useState(false);

  useEffect(() => {
    if (viewMode !== 'monthly') return;
    const recs = attendance.filter(a =>
      a.entityType === 'student' && a.classId === selectedClass && a.date >= gridStart && a.date <= gridEnd);
    const map = {};
    recs.forEach(a => { (map[a.entityId] = map[a.entityId] || {})[a.date] = a.status; });
    setGridData(map);
    setGridSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedClass, gridMonthYear]);

  const cycleGridCell = (studentId, dateStr) => {
    if (dateStr > today) return; // can't mark future attendance
    setGridData(prev => {
      const current = prev[studentId]?.[dateStr] ?? null;
      const next = GRID_STATUS_CYCLE[(GRID_STATUS_CYCLE.indexOf(current) + 1) % GRID_STATUS_CYCLE.length];
      const studentMap = { ...(prev[studentId] || {}) };
      if (next === null) delete studentMap[dateStr]; else studentMap[dateStr] = next;
      return { ...prev, [studentId]: studentMap };
    });
  };

  // One click to mark the whole day Present, then flip individual
  // exceptions — much faster than clicking every student for every day,
  // same "mark all, then flag exceptions" workflow as a paper register.
  const markDayAllPresent = (dateStr) => {
    if (dateStr > today) return;
    setGridData(prev => {
      const next = { ...prev };
      classStudents.forEach(s => { next[s.id] = { ...(next[s.id] || {}), [dateStr]: 'present' }; });
      return next;
    });
  };

  const studentMonthStats = (studentId) => {
    const byDate = gridData[studentId] || {};
    let present = 0, absent = 0, late = 0;
    Object.values(byDate).forEach(st => {
      if (st === 'present') present++; else if (st === 'absent') absent++; else if (st === 'late') late++;
    });
    const workingTotal = present + absent + late;
    const pct = workingTotal > 0 ? Math.round(((present + late) / workingTotal) * 100) : null;
    return { present, absent, late, pct };
  };

  const saveGrid = async () => {
    const datesWithData = new Set();
    Object.values(gridData).forEach(byDate => Object.keys(byDate).forEach(d => datesWithData.add(d)));
    const records = [];
    datesWithData.forEach(dateStr => {
      if (dateStr < gridStart || dateStr > gridEnd) return; // safety
      classStudents.forEach(s => {
        const status = gridData[s.id]?.[dateStr];
        if (status) records.push({
          id: generateId('ATT'), date: dateStr, entityType: 'student', entityId: s.id,
          classId: selectedClass, status, markedBy: currentUser?.profileId || 'STF-001',
          academicYear: academicYear || '',
        });
      });
    });
    if (!records.length) { toast?.error('Nothing marked to save'); return; }
    setGridSaving(true);
    try {
      await api.post('/attendance/bulk', { records });
      // The bulk endpoint deletes-then-inserts per date+class, so mirror
      // that locally: drop every existing record for this class on any date
      // that had data, then add back exactly what was just sent.
      setAttendance(prev => [
        ...prev.filter(a => !(a.classId === selectedClass && a.entityType === 'student' && datesWithData.has(a.date))),
        ...records,
      ]);
      toast?.success(`Saved ${MONTH_NAMES[gridMonthYear.m - 1]} ${gridMonthYear.y} for ${classStudents.length} students`);
      setGridSaved(true);
      setTimeout(() => setGridSaved(false), 3000);
    } catch (e) {
      toast?.error(`Save failed: ${e.message}`);
    }
    setGridSaving(false);
  };

  const statusConfig = {
    present: { label: 'Present', color: 'bg-green-100 text-green-700 border-green-300', icon: Check },
    absent: { label: 'Absent', color: 'bg-red-100 text-red-700 border-red-300', icon: X },
    late: { label: 'Late', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock },
  };

  const counts = { present: 0, absent: 0, late: 0 };
  Object.values(localAttendance).forEach(s => { if (counts[s] !== undefined) counts[s]++; });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Student Attendance</h1>
          <p className="text-gray-500 text-sm">{viewMode === 'daily' ? 'Mark daily attendance' : 'Mark a full month at once'}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {[['daily', 'Daily'], ['monthly', 'Monthly Grid']].map(([v, label]) => (
              <button key={v} onClick={() => setViewMode(v)}
                className={`px-4 py-1 text-sm font-medium ${viewMode === v ? 'bg-indigo-600 text-white' : 'bg-white hover:bg-gray-50'}`}>
                {label}
              </button>
            ))}
          </div>
          {viewMode === 'daily' ? (
            <button onClick={saveAttendance} disabled={saving} className={`flex items-center gap-2 px-4 py-1 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${saved ? 'bg-green-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
              <Save size={16} />{saving ? 'Saving...' : saved ? 'Saved!' : 'Save Attendance'}
            </button>
          ) : (
            <button onClick={saveGrid} disabled={gridSaving} className={`flex items-center gap-2 px-4 py-1 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${gridSaved ? 'bg-green-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
              <Save size={16} />{gridSaving ? 'Saving...' : gridSaved ? 'Saved!' : 'Save Month'}
            </button>
          )}
        </div>
      </div>

      {viewMode === 'daily' ? (
        <>
          <div className="flex gap-4 mb-6 flex-wrap">
            <div>
              <label className="block font-medium mb-1">Class</label>
              <select value={selectedClass} onChange={e=>setSelectedClass(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                {availableClasses.map(c=><option key={c.id} value={c.id}>{c.name} - Sec {c.section}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-medium mb-1">Date</label>
              <DateInput value={date} onChange={e=>setDate(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            </div>
          </div>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {Object.entries(counts).map(([status,count])=>(
              <div key={status} className={`rounded-lg px-4 py-1 text-center border ${statusConfig[status].color}`}>
                <div className="text-2xl font-bold">{count}</div>
                <div className="font-medium capitalize">{status}</div>
              </div>
            ))}
          </div>
          {/* Attendance List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-1 bg-gray-50 border-b border-gray-100">
              <p className="text-sm font-semibold">{classes.find(c=>c.id===selectedClass)?.name} - {classStudents.length} Students</p>
            </div>
            <div className="divide-y divide-gray-50">
              {classStudents.map((student, i) => {
                const status = localAttendance[student.id] || 'present';
                return (
                  <div key={student.id} className="flex items-center justify-between px-4 py-1 hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <span className="text-sm w-6">{i+1}</span>
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                        {student.firstName?.[0]}{student.lastName?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{student.firstName} {student.lastName}</p>
                        <p className="">Roll: {student.rollNumber}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {Object.entries(statusConfig).map(([s,cfg])=>(
                        <button key={s} onClick={()=>setStatus(student.id,s)}
                          className={`px-3 py-1 rounded-lg font-medium border transition-all ${status===s ? cfg.color : 'border-gray-200 hover:bg-gray-100'}`}>
                          {cfg.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {classStudents.length===0 && <div className="py-8 text-center">No students in this class</div>}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <div>
              <label className="block font-medium mb-1">Class</label>
              <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                {availableClasses.map(c => <option key={c.id} value={c.id}>{c.name} - Sec {c.section}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 self-end">
              <button onClick={prevGridMonth} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronLeft size={16} /></button>
              <span className="font-semibold min-w-[130px] text-center">{MONTH_NAMES[gridMonthYear.m - 1]} {gridMonthYear.y}</span>
              <button onClick={nextGridMonth} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronRight size={16} /></button>
            </div>
            <div className="flex gap-3 self-end ml-auto">
              {Object.entries(GRID_SHORT).map(([key, short]) => (
                <span key={key} className="flex items-center gap-1">
                  <span className="text-white rounded px-1.5 py-0.5 font-bold" style={{ background: GRID_COLOR[key] }}>{short}</span>
                  <span className="text-gray-500 capitalize">{key}</span>
                </span>
              ))}
              <span className="text-gray-400 italic">Click a cell to cycle · click the day number to mark everyone Present</span>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="text-sm border-collapse" style={{ minWidth: `${220 + gridDays.length * 34}px` }}>
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="sticky left-0 bg-gray-100 px-4 py-1.5 text-left font-semibold border-r border-gray-200 min-w-[200px]">Student</th>
                  {gridDays.map(({ day, dateStr, dow }) => {
                    const isWeekend = dow === 0 || dow === 6;
                    const isFuture = dateStr > today;
                    const isToday = dateStr === today;
                    return (
                      <th key={dateStr}
                        onClick={() => !isFuture && markDayAllPresent(dateStr)}
                        title={isFuture ? 'Future date' : `Mark everyone Present on ${formatDate(dateStr)}`}
                        className={`px-1 py-1 text-center font-semibold border-r border-gray-100 w-8 ${isFuture ? 'text-gray-300' : 'cursor-pointer hover:bg-indigo-100'} ${isWeekend ? 'bg-gray-50' : ''} ${isToday ? 'bg-indigo-50' : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className={isToday ? 'text-indigo-700' : ''}>{day}</span>
                          <span className="text-[9px] text-gray-400">{DAY_LETTERS[dow]}</span>
                        </div>
                      </th>
                    );
                  })}
                  <th className="px-2 py-1 text-center font-semibold border-l border-gray-200 min-w-[60px]">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {classStudents.map((student, i) => {
                  const stats = studentMonthStats(student.id);
                  return (
                    <tr key={student.id} className={i % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100'}>
                      <td className={`sticky left-0 px-4 py-1 border-r border-gray-200 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2">
                          <span className="w-6 text-right flex-shrink-0 text-gray-400">{student.rollNumber}</span>
                          <span className="font-medium truncate">{student.firstName} {student.lastName}</span>
                        </div>
                      </td>
                      {gridDays.map(({ dateStr, dow }) => {
                        const status = gridData[student.id]?.[dateStr];
                        const isWeekend = dow === 0 || dow === 6;
                        const isFuture = dateStr > today;
                        return (
                          <td key={dateStr}
                            onClick={() => cycleGridCell(student.id, dateStr)}
                            className={`text-center border-r border-gray-50 ${isFuture ? '' : 'cursor-pointer'} ${isWeekend ? 'bg-gray-50/60' : ''}`}>
                            <span className="inline-flex items-center justify-center w-6 h-5 rounded font-bold text-[11px]"
                              style={status ? { background: GRID_COLOR[status], color: '#fff' } : { color: isFuture ? '#e5e7eb' : '#d1d5db' }}>
                              {status ? GRID_SHORT[status] : (isFuture ? '' : '·')}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-2 py-1 text-center border-l border-gray-200">
                        {stats.pct === null ? <span className="text-gray-300">—</span> :
                          <span className={`font-bold ${stats.pct >= 75 ? 'text-green-600' : stats.pct >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>{stats.pct}%</span>}
                      </td>
                    </tr>
                  );
                })}
                {classStudents.length === 0 && (
                  <tr><td colSpan={gridDays.length + 2} className="py-8 text-center text-gray-400">No students in this class</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
export default StudentAttendance;
